import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import type { JSX, ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface MenuItem {
  key: string;
  label: string;
  icon?: ReactNode | undefined;
  onSelect?: (() => void) | undefined;
  /** Shown but not actionable: a screen that is not in this phase. */
  disabled?: boolean | undefined;
  /** Renders a separator above the item. */
  separatorBefore?: boolean | undefined;
  destructive?: boolean | undefined;
  /** Muted text at the right edge: the current value, or a keyboard shortcut. */
  hint?: string | undefined;
  /** `text` renders a non-interactive line (a note under a group); default `item`. */
  kind?: "item" | "text" | undefined;
}

export interface DropdownMenuProps {
  trigger: ReactNode;
  /** Accessible name for the menu, read when it opens. */
  label: string;
  items: readonly MenuItem[];
  align?: "start" | "center" | "end" | undefined;
  /** Non-interactive content above the items: who is signed in, for the account menu. */
  header?: ReactNode | undefined;
}

/** A menu of actions anchored to a trigger. Keyboard navigation comes from Radix. */
export function DropdownMenu({ trigger, label, items, align = "end", header }: DropdownMenuProps): JSX.Element {
  return (
    <RadixDropdown.Root>
      <RadixDropdown.Trigger asChild aria-label={label}>
        {trigger}
      </RadixDropdown.Trigger>
      <RadixDropdown.Portal>
        <RadixDropdown.Content
          align={align}
          sideOffset={6}
          className="z-50 min-w-48 rounded-control border border-line bg-surface p-1 shadow-overlay"
        >
          {header === undefined ? null : <div className="px-2 py-2">{header}</div>}
          {items.map((item) => (
            <div key={item.key}>
              {item.separatorBefore === true ? (
                <RadixDropdown.Separator className="my-1 h-px bg-line" />
              ) : null}
              {item.kind === "text" ? (
                <p className="max-w-64 px-2 pb-1 text-xs text-ink-subtle">{item.label}</p>
              ) : (
              <RadixDropdown.Item
                disabled={item.disabled === true}
                onSelect={() => {
                  item.onSelect?.();
                }}
                className={cn(
                  "flex h-8 cursor-default items-center gap-2 rounded-small px-2 text-base text-ink outline-none data-[disabled]:text-ink-subtle data-[highlighted]:bg-surface-muted [&_svg]:size-4 [&_svg]:text-ink-muted",
                  item.destructive === true && "text-danger-ink data-[highlighted]:bg-danger-soft [&_svg]:text-danger-ink",
                )}
              >
                {item.icon}
                <span className="flex-1">{item.label}</span>
                {item.hint === undefined ? null : (
                  <span className="text-xs text-ink-subtle">{item.hint}</span>
                )}
              </RadixDropdown.Item>
              )}
            </div>
          ))}
        </RadixDropdown.Content>
      </RadixDropdown.Portal>
    </RadixDropdown.Root>
  );
}
